/**
 * proposalEngine.js — Decomposition Lab Phase 1
 *
 * Pure UI-side proposal engine for Define Services.
 *
 * Reads only artifacts that DecomposeCodeView already loads — no new fetches,
 * no backend changes. Produces N grounded "proposal sets" the user can preview
 * and Apply into the workbench as proposed cards.
 *
 * Authority model is preserved:
 *   - every produced service is a *proposal* only (status: 'proposed')
 *   - nothing is auto-approved
 *   - applying a set adds cards alongside existing services; the user still
 *     confirms each one manually via the existing Confirm flow
 *
 * Phase 1 builders (all read-only, all explainable, all grounded):
 *   1. MI Balanced              — surface MI's recommended_services as a labeled set
 *   2. God-program split        — surface MI's god_object_decomposition entries
 *   3. Shared-data grouping     — Jaccard clustering on recommended_services[].shared_data[]
 *
 * Each builder degrades gracefully (returns null) if its inputs are missing
 * or too thin to produce a useful proposal set.
 *
 * Output shape is forward-compatible with the future backend
 * `proposal_sets.json` artifact (see 2026-04-26_decomposition_lab_technical_plan.md).
 */

// ── Phase 1 schema version ────────────────────────────────────────────────────
export const PROPOSAL_ENGINE_SCHEMA_VERSION = 'decomp_lab_v1'

/**
 * Public entry point.
 *
 * @param {object} ctx
 * @param {object|null} ctx.decompositionData  MI decomposition_strategy.json
 * @returns {{
 *   schema_version: string,
 *   generated_at: string,
 *   proposal_sets: Array<ProposalSet>
 * }}
 */
export function computeProposalSets(ctx) {
  const decomp = ctx?.decompositionData || null

  const sets = []
  const miBalanced = buildMiBalancedSet(decomp)
  if (miBalanced) sets.push(miBalanced)

  const godSplit = buildGodProgramSplitSet(decomp)
  if (godSplit) sets.push(godSplit)

  const sharedData = buildSharedDataGroupingSet(decomp)
  if (sharedData) sets.push(sharedData)

  return {
    schema_version: PROPOSAL_ENGINE_SCHEMA_VERSION,
    generated_at: new Date().toISOString(),
    proposal_sets: sets,
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function stripJavaExt(s) {
  return typeof s === 'string' ? s.replace(/\.java$/i, '') : s
}

function safeArr(x) {
  return Array.isArray(x) ? x : []
}

function makeService({ id, name, domain, members, cobolOrigins, rationale, sharedData }) {
  return {
    id,
    proposed_name: name,
    domain: domain || 'Unclassified',
    members: members || [],
    cobol_origins: cobolOrigins || [],
    rationale: rationale || '',
    shared_data: sharedData || [],
  }
}

// ── 1. MI Balanced ────────────────────────────────────────────────────────────
//
// Re-package MI's recommended_services as a labeled "MI Balanced" proposal set.
// This is the same data DecomposeCodeView already renders as AI cards — but
// labeled here as one of N alternatives. Always returns a set when MI provided
// at least one recommended service.
export function buildMiBalancedSet(decomp) {
  const recs = safeArr(decomp?.recommended_services)
  if (recs.length === 0) return null

  const services = recs.map((svc, i) => {
    const programs = safeArr(svc.programs)
    const cobolOrigins = [...new Set(programs.map(p => stripJavaExt(p)))]
    const members = programs.map(p => ({
      name: p,
      cobol: stripJavaExt(p),
      loc: 0,
      extDeps: 0,
    }))
    return makeService({
      id: `ps_mi_balanced_${i}`,
      name: svc.service_name || `Service ${i + 1}`,
      domain: svc.business_capability || 'Unclassified',
      members,
      cobolOrigins,
      sharedData: safeArr(svc.shared_data),
      rationale: `Monolith Identifier grouped ${programs.length} program${programs.length !== 1 ? 's' : ''} by ${svc.business_capability || 'capability'}; extraction complexity ${svc.extraction_complexity || 'unknown'}.`,
    })
  })

  return {
    id: 'mi_balanced',
    name: 'MI Balanced',
    algorithm: 'mi_repackage',
    algorithm_family: 'structural',
    rationale: 'Monolith Identifier’s default grouping by business capability (cohesion + coupling).',
    services,
  }
}

// ── 2. God-program split ──────────────────────────────────────────────────────
//
// Surface MI's god_object_decomposition entries as a "Split god programs"
// proposal set. For each god program, emit one service per detected capability
// (or one service per proposed_split entry if MI provided that). The same
// COBOL program will appear as a member in each split — the user resolves
// ownership when they confirm one and dismiss the others (existing flow).
//
// Returns null if no god_object_decomposition or all entries empty.
export function buildGodProgramSplitSet(decomp) {
  const plans = safeArr(decomp?.god_object_decomposition)
  if (plans.length === 0) return null

  const services = []

  plans.forEach((plan, pi) => {
    const program = plan?.program
    if (!program) return

    // Preferred: explicit proposed_split entries from MI
    const explicitSplits = safeArr(plan.proposed_split)
    if (explicitSplits.length > 0) {
      explicitSplits.forEach((sp, si) => {
        const name = sp?.name || `${program}Part${si + 1}Service`
        services.push(makeService({
          id: `ps_god_split_${pi}_${si}`,
          name,
          domain: sp?.capability || sp?.business_capability || 'Decomposed',
          members: [{ name: program, cobol: program, loc: 0, extDeps: 0 }],
          cobolOrigins: [program],
          rationale: `God program ${program} — proposed split: ${name}${sp?.paragraphs ? ` (${safeArr(sp.paragraphs).length} paragraphs)` : ''}.`,
        }))
      })
      return
    }

    // Fallback: derive one service per detected capability
    const caps = safeArr(plan.current_capabilities)
    if (caps.length === 0) {
      // No capability info — emit a single placeholder service so user sees the candidate
      services.push(makeService({
        id: `ps_god_split_${pi}_0`,
        name: `${toPascal(program)}DecomposedService`,
        domain: 'Decomposed',
        members: [{ name: program, cobol: program, loc: 0, extDeps: 0 }],
        cobolOrigins: [program],
        rationale: `God program ${program} flagged for decomposition; no capability breakdown provided.`,
      }))
      return
    }

    caps.forEach((cap, ci) => {
      const capLabel = typeof cap === 'string' ? cap : (cap?.name || `Capability${ci + 1}`)
      services.push(makeService({
        id: `ps_god_split_${pi}_${ci}`,
        name: `${toPascal(program)}${toPascal(capLabel)}Service`,
        domain: capLabel,
        members: [{ name: program, cobol: program, loc: 0, extDeps: 0 }],
        cobolOrigins: [program],
        rationale: `God program ${program} — capability "${capLabel}" extracted as candidate boundary.`,
      }))
    })
  })

  if (services.length === 0) return null

  return {
    id: 'god_split',
    name: 'Split god programs',
    algorithm: 'god_program_split',
    algorithm_family: 'structural',
    rationale: 'Each god program gets one candidate service per detected capability — confirm the splits you want.',
    services,
  }
}

// ── 3. Shared-data grouping ───────────────────────────────────────────────────
//
// Cluster MI's recommended_services by shared_data overlap (Jaccard ≥ threshold).
// Produces an "Aligned by shared data" set where services that share data
// structures are merged into one proposal.
//
// Degrades gracefully:
//   - returns null if no service has shared_data
//   - returns null if every service ends up in its own cluster (no overlap)
//   - returns null if all services collapse into one cluster (no useful split)
export function buildSharedDataGroupingSet(decomp) {
  const recs = safeArr(decomp?.recommended_services)
  if (recs.length < 2) return null

  // Build (idx → Set(shared_data items)) for services that have any shared data
  const dataSets = recs.map(svc => new Set(safeArr(svc.shared_data)))
  const haveData = dataSets.some(s => s.size > 0)
  if (!haveData) return null

  // Union-find clustering on Jaccard ≥ 0.34 (≈ "share at least one of three")
  const THRESHOLD = 0.34
  const parent = recs.map((_, i) => i)
  const find = (x) => { while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x] } return x }
  const union = (a, b) => { const ra = find(a), rb = find(b); if (ra !== rb) parent[ra] = rb }

  for (let i = 0; i < recs.length; i++) {
    for (let j = i + 1; j < recs.length; j++) {
      const a = dataSets[i], b = dataSets[j]
      if (a.size === 0 || b.size === 0) continue
      let inter = 0
      a.forEach(x => { if (b.has(x)) inter++ })
      const uni = a.size + b.size - inter
      if (uni === 0) continue
      const jacc = inter / uni
      if (jacc >= THRESHOLD) union(i, j)
    }
  }

  // Group by root
  const groups = new Map()
  recs.forEach((_, i) => {
    const r = find(i)
    if (!groups.has(r)) groups.set(r, [])
    groups.get(r).push(i)
  })

  const clusters = [...groups.values()]
  // Need at least one merged cluster (size>1) AND not collapse to a single cluster
  const merged = clusters.filter(c => c.length > 1)
  if (merged.length === 0) return null
  if (clusters.length === 1) return null

  const services = clusters.map((idxs, gi) => {
    const programs = []
    const cobolOrigins = new Set()
    const sharedData = new Set()
    const capabilities = new Set()
    idxs.forEach(idx => {
      const svc = recs[idx]
      safeArr(svc.programs).forEach(p => {
        if (!programs.find(x => x.name === p)) {
          programs.push({ name: p, cobol: stripJavaExt(p), loc: 0, extDeps: 0 })
        }
        cobolOrigins.add(stripJavaExt(p))
      })
      safeArr(svc.shared_data).forEach(d => sharedData.add(d))
      if (svc.business_capability) capabilities.add(svc.business_capability)
    })

    const capLabel = [...capabilities].slice(0, 2).join('+') || 'Shared'
    const merged = idxs.length > 1
    return makeService({
      id: `ps_shared_data_${gi}`,
      name: merged
        ? `${toPascal(capLabel)}DataAlignedService`
        : (recs[idxs[0]].service_name || `Service ${gi + 1}`),
      domain: capLabel,
      members: programs,
      cobolOrigins: [...cobolOrigins],
      sharedData: [...sharedData],
      rationale: merged
        ? `Merged ${idxs.length} MI services that share data: ${[...sharedData].slice(0, 4).join(', ')}${sharedData.size > 4 ? '…' : ''}.`
        : `Standalone — no significant shared-data overlap with other services.`,
    })
  })

  return {
    id: 'shared_data',
    name: 'Aligned by shared data',
    algorithm: 'shared_data_jaccard',
    algorithm_family: 'data',
    rationale: `Services that share ≥${Math.round(THRESHOLD * 100)}% of their data structures (Jaccard) are merged.`,
    services,
  }
}

// ── tiny PascalCase helper ────────────────────────────────────────────────────
function toPascal(s) {
  if (!s) return ''
  return String(s)
    .replace(/[^A-Za-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join('')
}

/**
 * Build a normalized membership key for a service.
 *
 * Phase 1.x equivalence rule: two services are considered the same boundary
 * when their normalized COBOL-origin sets are equal AND non-empty.
 * Falls back to member-name set when cobol_origins is empty (manual cards
 * built from Java-only inventories may have empty cobol_origins).
 *
 * Normalization:
 *   - strip ".java" extension
 *   - lowercase
 *   - dedupe + sort (so [A,B] === [B,A])
 *
 * Returns "" if there is no useful identity (skip duplicate check).
 *
 * @param {object} svc  Service-shaped object — accepts both proposal services
 *                      ({cobol_origins[], members[{name}]}) and workbench
 *                      cards ({cobolOrigins[], members[{name,cobol}]}).
 */
export function membershipKey(svc) {
  if (!svc) return ''
  const origins = (svc.cobol_origins || svc.cobolOrigins || [])
    .map(s => stripJavaExt(String(s || '')).toLowerCase())
    .filter(Boolean)
  if (origins.length > 0) {
    return 'co:' + [...new Set(origins)].sort().join('|')
  }
  // Fallback: member names (covers Java-only inventories)
  const names = (svc.members || [])
    .map(m => stripJavaExt(String(m?.name || m?.cobol || '')).toLowerCase())
    .filter(Boolean)
  if (names.length > 0) {
    return 'mn:' + [...new Set(names)].sort().join('|')
  }
  return ''
}

/**
 * Filter a proposal set's services down to only those NOT already represented
 * in the existing workbench. Used by Apply to prevent duplicate cards.
 *
 * @param {object}        set            ProposalSet
 * @param {Array<object>} existingSvcs   Current workbench services (MI +
 *                                       manual + approved — anything visible)
 * @returns {{
 *   newServices:        Array<object>,   // proposal services to actually apply
 *   duplicateServices:  Array<object>,   // proposal services skipped as duplicates
 *   newCount:           number,
 *   duplicateCount:     number
 * }}
 */
export function filterNewProposalServices(set, existingSvcs) {
  const proposalServices = safeArr(set?.services)
  const existing = safeArr(existingSvcs)
  const existingKeys = new Set(
    existing.map(membershipKey).filter(k => k !== '')
  )
  const newServices = []
  const duplicateServices = []
  const seenInSet = new Set()  // also dedupe within the set itself
  proposalServices.forEach(s => {
    const k = membershipKey(s)
    if (k === '') {
      // No useful identity — treat as new (caller can still skip if desired)
      newServices.push(s)
      return
    }
    if (existingKeys.has(k) || seenInSet.has(k)) {
      duplicateServices.push(s)
    } else {
      newServices.push(s)
      seenInSet.add(k)
    }
  })
  return {
    newServices,
    duplicateServices,
    newCount: newServices.length,
    duplicateCount: duplicateServices.length,
  }
}

/**
 * Convert a ProposalSet's services into manualService-shaped cards ready
 * to be appended to DecomposeCodeView's `manualServices` state by Apply.
 *
 * Caller is responsible for filtering against already-approved services
 * (see DecomposeCodeView.handleApplyProposalSet).
 *
 * @param {object} set         ProposalSet
 * @param {object} [opts]
 * @param {string} [opts.idPrefix='svc-lab']  Prefix for the generated card ids
 */
export function proposalSetToManualServices(set, opts = {}) {
  const idPrefix = opts.idPrefix || 'svc-lab'
  const stamp = Date.now()
  return safeArr(set?.services).map((s, i) => ({
    id: `${idPrefix}-${set.id}-${stamp}-${i}`,
    name: s.proposed_name,
    domain: s.domain || 'Unclassified',
    classes: safeArr(s.members).length,
    loc: 0,
    cohesion: 'medium',
    couplingOut: 0,
    businessHint: s.domain || '',
    status: 'proposed',
    cobolOrigins: safeArr(s.cobol_origins),
    members: safeArr(s.members),
    whySignals: {
      dataShared: safeArr(s.shared_data).length
        ? `Shared data: ${safeArr(s.shared_data).join(', ')}`
        : 'From Decomposition Lab',
      internalDensity: `Provenance: ${set.algorithm}`,
      circular: 'N/A',
      aiConfidence: null,
    },
    deps: [],
    isManual: true,                 // Treated as manual so existing edit flow works
    provenance: {                   // Extra metadata for UI badge / debugging
      source: 'decomposition_lab',
      proposal_set_id: set.id,
      proposal_set_name: set.name,
      algorithm: set.algorithm,
      algorithm_family: set.algorithm_family,
      rationale: s.rationale,
    },
  }))
}
